import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft, MessageCircle, Sparkles } from "lucide-react";
import { Toaster, toast } from "sonner";
import { CLINIC, LogoTile, doctorDisplay, buildWhatsAppBrandedMessage, doctorPhotoUrl, brandColor, BrandAccentStyle } from "../lib/clinic";
import { openWhatsAppRaw } from "../lib/whatsapp";
import { DIET_TYPES } from "../lib/dietTypes";
import AdvancedDietPrintout from "../components/AdvancedDietPrintout";

export default function PrintDietPlan() {
  const { planId } = useParams();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/diet/plans/${planId}`);
        setPlan(r.data.plan);
      } catch (e) {
        console.warn("load plan failed:", e);
        toast.error("Could not load diet plan");
      }
    })();
  }, [planId]);

  if (!plan) return <div className="p-8 text-sm text-slate-500">Loading diet plan…</div>;
  // v2 (Advanced) plans have a completely different structured schema and
  // render via a dedicated 3-page A4 printout component. v1 plans continue
  // rendering below unchanged.
  if (plan.schema_version === 2) {
    return <AdvancedDietPrintout plan={plan} />;
  }
  const p = plan.patient_snapshot || {};
  const dietType = DIET_TYPES.find((d) => d.id === plan.preferences?.diet_type);
  const date = new Date(plan.issued_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const isMr = plan.language === "mr";
  const dietName = dietType ? (isMr ? dietType.name_mr : dietType.name_en) : plan.preferences?.diet_type;

  const openWhatsApp = async () => {
    if (!plan.plan_text) return;
    const patient = plan.patient_snapshot || {};
    const msg = buildWhatsAppBrandedMessage({
      lang: isMr ? "mr" : "en",
      title: dietName,
      body: plan.plan_text,
      patientName: patient.name,
      kind: "diet",
      dateStr: date,
    });
    // Resolve the patient's mobile so WhatsApp opens the right chat directly.
    let mobile = patient.mobile || "";
    if (!mobile && plan.patient_id) {
      try {
        const r = await api.get(`/patients/${plan.patient_id}`);
        mobile = r.data?.patient?.mobile || "";
      } catch (e) { console.debug("[silent]", e); }
    }
    openWhatsAppRaw(mobile, msg);
  };

  return (
    <div className="min-h-screen bg-slate-50 print-shell">
      <Toaster position="top-right" richColors />
      <div className="print-toolbar sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-2 no-print">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="diet-print-back"><ArrowLeft size={14} className="mr-1" /> Back</Button>
        <div className="text-sm text-slate-600">Diet Plan · {p.name}</div>
        <div className="ml-auto flex gap-2">
          {plan.patient_id && (
            <Button size="sm" variant="outline" onClick={() => nav(`/diet/${plan.patient_id}?tab=advanced`)} className="border-indigo-300 text-indigo-700" data-testid="diet-upgrade-advanced-btn" title="Regenerate this plan as a condition-aware Advanced Diet Chart">
              <Sparkles size={14} className="mr-1" /> Upgrade to Advanced
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={openWhatsApp} className="border-emerald-300 text-emerald-700" data-testid="diet-wa-btn">
            <MessageCircle size={14} className="mr-1" /> WhatsApp
          </Button>
          <Button size="sm" onClick={() => window.print()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="diet-print-btn">
            <Printer size={14} className="mr-1" /> Print / Save PDF
          </Button>
        </div>
      </div>

      <BrandAccentStyle scope="brand-scope" />
      <div className="max-w-[820px] mx-auto my-6 bg-white border border-slate-200 shadow-sm print-page brand-scope" style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }} data-testid="diet-print-page">
        <div className="p-8">
          <header className="flex justify-between items-start pb-4 border-b-2" style={{ borderColor: brandColor() }} data-testid="diet-branded-header">
            <div className="flex gap-4 items-start">
              <LogoTile className="w-16 h-16 flex-shrink-0" padding="p-1.5" />
              <img src={doctorPhotoUrl()} alt={CLINIC.doctorName} className="h-16 w-16 rounded-full object-cover object-top border-2 flex-shrink-0" style={{ borderColor: brandColor() }} data-testid="diet-print-doctor-photo" />
              <div>
                <div className="font-heading text-xl font-bold leading-tight" style={{ color: brandColor() }}>{CLINIC.name}</div>
                <div className="text-xs text-slate-700 mt-1 font-medium">{doctorDisplay(CLINIC.doctorName)}</div>
                <div className="text-[10px] text-slate-500 leading-snug">{CLINIC.qualifications}</div>
                <div className="text-[10px] text-slate-500">{CLINIC.regNo}</div>
                <div className="text-[10px] font-semibold mt-0.5" style={{ color: brandColor() }}>{CLINIC.specialties}</div>
              </div>
            </div>
            <div className="text-right text-[10px] text-slate-600 max-w-[220px] leading-snug">
              {CLINIC.address && <div>{CLINIC.address}</div>}
              {(CLINIC.phones || []).length > 0 && <div className="mt-1 tabular">{CLINIC.phones.join(" · ")}</div>}
              {CLINIC.timings && <div className="mt-1 font-medium">{CLINIC.timings}</div>}
            </div>
          </header>

          <section className="grid grid-cols-4 gap-2 text-[11px] mt-4 py-3 border-b border-slate-200">
            <div><span className="uppercase text-slate-500">Name:</span> <span className="font-medium">{p.name}</span></div>
            <div><span className="uppercase text-slate-500">Age/Sex:</span> {p.age} / {p.sex}</div>
            <div><span className="uppercase text-slate-500">Plan ID:</span> <span className="tabular">{plan.plan_id}</span></div>
            <div><span className="uppercase text-slate-500">Date:</span> <span className="tabular">{date}</span></div>
          </section>

          <section className="mt-4 py-3 border-b border-slate-200 text-center">
            <div className="uppercase text-[10px] tracking-[0.25em] text-emerald-600 font-semibold">{isMr ? "वैयक्तिक आहार आराखडा" : "Personalised Diet Plan"}</div>
            <div className="font-heading text-2xl font-bold text-slate-900 mt-0.5">{dietName}</div>
          </section>

          <section className="mt-4 grid grid-cols-4 gap-2 text-[10.5px] text-slate-700">
            <MetricPill label="BMI" value={plan.preferences?.bmi} />
            <MetricPill label={isMr ? "देखभाल कॅलरी" : "Maintenance"} value={`${plan.preferences?.tdee || "—"} kcal`} />
            <MetricPill label={isMr ? "क्लिनिकल लक्ष्य" : "Clinical Target"} value={`${plan.preferences?.target_calories || "—"} kcal`} bold />
            <MetricPill label={isMr ? "प्रकार" : "Choice"} value={plan.preferences?.choice} />
          </section>

          <section className="mt-4 border border-slate-200 rounded p-4 bg-slate-50/40">
            <pre className="whitespace-pre-wrap text-[12px] leading-relaxed text-slate-800 font-sans">{plan.plan_text}</pre>
          </section>

          <section className="py-3 border-t border-slate-200 mt-6 flex justify-between items-end rx-signature-block">
            <div className="text-[11px] text-slate-500">
              <div className="uppercase text-[9px] tracking-wider text-slate-500 font-semibold">Plan No.</div>
              <div className="tabular font-medium text-slate-800">{plan.plan_id}</div>
            </div>
            <div className="text-right max-w-[62%]">
              <div className="border-t border-slate-800 pt-1 mt-8 text-[11px]">
                <div className="font-medium">{doctorDisplay(CLINIC.doctorName)}</div>
                <div className="text-[9.5px] text-slate-600 leading-snug mt-0.5">{CLINIC.qualifications}</div>
              </div>
            </div>
          </section>

          <footer className="mt-4 pt-3 border-t border-slate-200 text-[9px] text-slate-500 leading-relaxed print-doctor-footer">
            This diet plan is issued by <strong>{doctorDisplay(CLINIC.doctorName)}, ({CLINIC.qualifications})</strong>, {CLINIC.name}. Please follow this plan for the recommended period and check in for a follow-up review. Modify only after clinician approval.
          </footer>
        </div>

        <div
          className="print-marketing-footer text-white"
          style={{ backgroundColor: brandColor(), WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
          data-testid="diet-branded-footer"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap px-4 py-3">
            <div className="min-w-0">
              <div className="text-[12px] font-extrabold">{CLINIC.name}</div>
              {CLINIC.address && <div className="text-[9.5px] opacity-90 leading-snug">{CLINIC.address}</div>}
              <div className="text-[9.5px] opacity-90 flex flex-wrap items-center gap-x-3 mt-0.5">
                {(CLINIC.phones || []).length > 0 && <span>📞 {CLINIC.phones.join(" · ")}</span>}
                {CLINIC.website && <span>🌐 {CLINIC.website}</span>}
                {CLINIC.email && <span>✉ {CLINIC.email}</span>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] font-semibold">{doctorDisplay(CLINIC.doctorName)}</div>
              {CLINIC.timings && <div className="text-[9px] opacity-90">{CLINIC.timings}</div>}
            </div>
          </div>
          <div className="text-[8px] opacity-80 px-4 pb-2 pt-1.5 border-t border-white/25">
            {isMr
              ? "पुरावा-आधारित पोषण — ICMR / ADA मार्गदर्शक तत्त्वांवर आधारित वैयक्तिक आराखडा. ४–६ आठवड्यांनी पुनरावलोकनासाठी या."
              : "Evidence-based nutrition — personalised on ICMR / DGCI / ADA guidelines. Book a review after 4–6 weeks for re-calibration."}
            {" · "}{plan.plan_id}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricPill({ label, value, bold }) {
  return (
    <div className="border border-slate-200 rounded px-2 py-1 bg-white">
      <div className="text-[8px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-[12px] ${bold ? "font-bold text-emerald-800" : "text-slate-800"}`}>{value || "—"}</div>
    </div>
  );
}
